import { apiClient } from './api'

export interface Merchant {
  id: number
  name: string
  email: string
  company_name: string
  description?: string
  status: 'enabled' | 'disabled' | 'pending' | 'suspended'
  website_url?: string
  success_redirect_url?: string
  cancel_redirect_url?: string
  webhook_url?: string
  commission_rate: number
  currency: {
    id: number
    code: string
    name: string
  }
  country?: {
    id: number
    name: string
    code: string
  }
  environment: 'production' | 'sandbox'
  is_verified: boolean
  created_at: string
  updated_at: string
  users_count: number
  submerchants_count: number
  api_key?: string
}

export interface CreateMerchantData {
  name: string
  email: string
  company_name: string
  description?: string
  website_url?: string
  success_redirect_url?: string
  cancel_redirect_url?: string
  webhook_url?: string
  commission_rate?: number
  currency_id: number
  country_id?: number
  environment?: 'production' | 'sandbox'
}

export interface UpdateMerchantData {
  name?: string
  email?: string
  company_name?: string
  description?: string
  website_url?: string
  success_redirect_url?: string
  cancel_redirect_url?: string
  webhook_url?: string
  commission_rate?: number
  currency_id?: number
  country_id?: number
  status?: 'enabled' | 'disabled' | 'pending' | 'suspended'
  environment?: 'production' | 'sandbox'
}

export interface MerchantListParams {
  page?: number
  page_size?: number
  search?: string
  status?: string
  environment?: string
  country?: number
  currency?: number
}

export interface MerchantListResponse {
  success: boolean
  message: string
  data: {
    results: Merchant[]
    count: number
    next: string | null
    previous: string | null
  }
}

export interface MerchantResponse {
  success: boolean
  message: string
  data: Merchant
}

export interface MerchantStats {
  total_merchants: number
  active_merchants: number
  pending_merchants: number
  suspended_merchants: number
  total_revenue: number
  total_transactions: number
}

export interface MerchantStatsResponse {
  success: boolean
  message: string
  data: MerchantStats
}

export interface MerchantUser {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  is_active: boolean
  is_verified: boolean
  created_at: string
  last_login?: string
}

export interface MerchantUsersResponse {
  success: boolean
  message: string
  data: MerchantUser[]
}

export interface CreateMerchantUserData {
  email: string
  first_name: string
  last_name: string
  role: 'merchant_admin' | 'merchant_user'
  company_name?: string
  phone_number?: string
  country?: number
  send_welcome_email?: boolean
}

export interface CreateMerchantUserResponse {
  success: boolean
  message: string
  user: MerchantUser
  temporary_password: string
  email_sent: boolean
}

/**
 * Merchant Service for managing merchants and merchant-related operations
 */
export const merchantService = {
  /**
   * Get list of merchants (admin only)
   */
  async getMerchants(params?: MerchantListParams): Promise<MerchantListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.environment) queryParams.append('environment', params.environment)
    if (params?.country) queryParams.append('country', params.country.toString())
    if (params?.currency) queryParams.append('currency', params.currency.toString())
    
    const queryString = queryParams.toString()
    const url = queryString ? `/merchants/?${queryString}` : '/merchants/'
    
    const response = await apiClient.get(url) as any
    // Backend returns data directly, not wrapped in success/data format
    return {
      success: true,
      message: 'Merchants retrieved successfully',
      data: response
    }
  },

  /**
   * Get merchant by ID
   */
  async getMerchant(merchantId: number): Promise<MerchantResponse> {
    const response = await apiClient.get(`/merchants/${merchantId}/`) as Merchant
    return {
      success: true,
      message: 'Merchant retrieved successfully',
      data: response
    }
  },

  /**
   * Create a new merchant (admin only)
   */
  async createMerchant(data: CreateMerchantData): Promise<MerchantResponse> {
    const response = await apiClient.post('/merchants/', data) as Merchant
    return {
      success: true,
      message: 'Merchant created successfully',
      data: response
    }
  },

  /**
   * Update merchant
   */
  async updateMerchant(merchantId: number, data: UpdateMerchantData): Promise<MerchantResponse> {
    const response = await apiClient.patch(`/merchants/${merchantId}/`, data) as Merchant
    return {
      success: true,
      message: 'Merchant updated successfully',
      data: response
    }
  },

  /**
   * Delete merchant (admin only)
   */
  async deleteMerchant(merchantId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/merchants/${merchantId}/`)
  },

  /**
   * Get merchant statistics (admin only)
   */
  async getMerchantStats(): Promise<MerchantStatsResponse> {
    return apiClient.get('/merchants/stats/')
  },

  /**
   * Get users for a specific merchant
   */
  async getMerchantUsers(merchantId: number): Promise<MerchantUsersResponse> {
    const response = await apiClient.get(`/merchants/${merchantId}/users/`) as MerchantUser[]
    return {
      success: true,
      message: 'Merchant users retrieved successfully',
      data: response
    }
  },

  /**
   * Create a user for a specific merchant
   */
  async createMerchantUser(merchantId: number, data: CreateMerchantUserData): Promise<CreateMerchantUserResponse> {
    return apiClient.post(`/merchants/${merchantId}/create_user/`, data)
  },

  /**
   * Create an admin user for a specific merchant
   */
  async createMerchantAdminUser(merchantId: number, data: CreateMerchantUserData): Promise<CreateMerchantUserResponse> {
    return apiClient.post(`/merchants/${merchantId}/create_admin_user/`, data)
  },

  /**
   * Get submerchants for a specific merchant
   */
  async getMerchantSubmerchants(merchantId: number): Promise<{ success: boolean; message: string; data: any[] }> {
    const response = await apiClient.get(`/merchants/${merchantId}/submerchants/`) as any[]
    return {
      success: true,
      message: 'Submerchants retrieved successfully',
      data: response
    }
  },

  /**
   * Get current user's merchant (for merchant_admin)
   * Note: This requires the merchant ID from the user object
   */
  async getMyMerchant(merchantId: number): Promise<MerchantResponse> {
    const response = await apiClient.get(`/merchants/${merchantId}/`) as Merchant
    // Backend returns data directly, not wrapped in success/data format
    return {
      success: true,
      message: 'Merchant details retrieved successfully',
      data: response
    }
  },

  /**
   * Update current user's merchant (for merchant_admin)
   */
  async updateMyMerchant(merchantId: number, data: UpdateMerchantData): Promise<MerchantResponse> {
    const response = await apiClient.patch(`/merchants/${merchantId}/`, data) as Merchant
    // Backend returns data directly, not wrapped in success/data format
    return {
      success: true,
      message: 'Merchant updated successfully',
      data: response
    }
  },

  /**
   * Get my merchant users (for merchant_admin)
   */
  async getMyMerchantUsers(merchantId: number): Promise<MerchantUsersResponse> {
    const response = await apiClient.get(`/merchants/${merchantId}/users/`) as MerchantUser[]
    // Backend returns data directly, not wrapped in success/data format
    return {
      success: true,
      message: 'Merchant users retrieved successfully',
      data: response
    }
  },

  /**
   * Create user for my merchant (for merchant_admin)
   */
  async createMyMerchantUser(merchantId: number, data: CreateMerchantUserData): Promise<CreateMerchantUserResponse> {
    return apiClient.post(`/merchants/${merchantId}/create_user/`, data)
  },

  /**
   * Get my merchant submerchants (for merchant_admin)
   */
  async getMyMerchantSubmerchants(merchantId: number): Promise<{ success: boolean; message: string; data: any[] }> {
    const response = await apiClient.get(`/merchants/${merchantId}/submerchants/`) as any[]
    // Backend returns data directly, not wrapped in success/data format
    return {
      success: true,
      message: 'Submerchants retrieved successfully',
      data: response
    }
  },
}
